import type { MatchFound, Mode } from "./types"
import { joow } from "./shell"
import { getRealtimeToken, isBridgeEmbeddedCandidate } from "./bridge"
import { authAuthorizationHeader } from "./auth"
import { requestCallMedia, setMediaStreamEnabled, stopMediaStream } from "./media"
import { estimateCallMediaProfile } from "./bandwidth"
import { Room, RoomEvent, Track } from "livekit-client"

export type ConnectionState = "connecting" | "connected" | "reconnecting" | "unavailable" | "failed"

export interface RemoteMediaParticipant {
  id: string
  name?: string
  audioStream?: MediaStream
  videoStream?: MediaStream
  hasAudio: boolean
  hasVideo: boolean
}

export interface RealtimeConnection {
  state: ConnectionState
  provider: "mock" | "host" | "livekit" | "joowcore" | "unavailable"
  localStream?: MediaStream
  remoteStream?: MediaStream
  remoteParticipants?: RemoteMediaParticipant[]
  peerCount?: number
  error?: string
  onState?(listener: (state: ConnectionState) => void): () => void
  onLocalStreamChange?(listener: (stream?: MediaStream) => void): () => void
  onRemoteStreamChange?(listener: (stream?: MediaStream) => void): () => void
  onRemoteParticipantsChange?(listener: (participants: RemoteMediaParticipant[]) => void): () => void
  onPeerCountChange?(listener: (count: number) => void): () => void
  setMicEnabled(enabled: boolean): Promise<void>
  setCameraEnabled(enabled: boolean): Promise<void>
  resumeAfterInterruption?(state: { mic: boolean; camera: boolean }): Promise<void>
  disconnect(reason: "end" | "next" | "unmount"): Promise<void>
}

interface RemoteParticipantEntry {
  id: string
  name?: string
  audioStream: MediaStream
  videoStream: MediaStream
}

interface HostRealtimeSDK {
  connect(input: {
    sessionId: string
    mode: Mode
    signaling: MatchFound["signaling"]
    media: MatchFound["media"]
  }): Promise<RealtimeConnection>
}

declare global {
  interface Window {
    JoowFakRealtime?: HostRealtimeSDK
  }
}

const forceMock = import.meta.env.VITE_API_MOCK === "true"
const apiBase = import.meta.env.VITE_API_BASE || "/api"

export async function connectRealtime(match: MatchFound, mode: Mode): Promise<RealtimeConnection> {
  if (forceMock || match.signaling.url.includes("mock-signaling")) {
    return mockConnection(mode)
  }
  if (match.realtimeProvider === "livekit") {
    return liveKitConnection(match, mode)
  }
  if (match.realtimeProvider === "joowcore") {
    return joowcoreConnection(match, mode)
  }
  if (window.joow || window.Joow) {
    return shellConnection(match.sessionId, mode)
  }
  if (window.JoowFakRealtime) {
    return window.JoowFakRealtime.connect({
      sessionId: match.sessionId,
      mode,
      signaling: match.signaling,
      media: match.media,
    })
  }
  if (isBridgeEmbeddedCandidate()) {
    return joowcoreConnection(match, mode)
  }
  return unavailableConnection("اتصال زنده هنوز روی این محیط فعال نیست")
}

async function liveKitConnection(match: MatchFound, mode: Mode): Promise<RealtimeConnection> {
  let currentState: ConnectionState = "connecting"
  let currentPeerCount = 0
  const stateListeners = new Set<(state: ConnectionState) => void>()
  const localStreamListeners = new Set<(stream?: MediaStream) => void>()
  const remoteStreamListeners = new Set<(stream?: MediaStream) => void>()
  const remoteParticipantListeners = new Set<(participants: RemoteMediaParticipant[]) => void>()
  const peerCountListeners = new Set<(count: number) => void>()
  const remoteParticipants = new Map<string, RemoteParticipantEntry>()
  const debugLiveKit = import.meta.env.DEV || import.meta.env.VITE_LIVEKIT_DEBUG === "true"
  const logLiveKit = (message: string, details?: unknown) => {
    if (!debugLiveKit) return
    if (details === undefined) console.info(`[JoowFak LiveKit] ${message}`)
    else console.info(`[JoowFak LiveKit] ${message}`, details)
  }
  const setState = (state: ConnectionState) => {
    currentState = state
    stateListeners.forEach((listener) => listener(state))
  }
  const setPeerCount = (count: number) => {
    currentPeerCount = Math.max(0, count)
    peerCountListeners.forEach((listener) => listener(currentPeerCount))
  }
  const notifyLocalStream = () => {
    const snapshot = localStream ? new MediaStream(localStream.getTracks().filter((track) => track.readyState === "live")) : undefined
    localStreamListeners.forEach((listener) => listener(snapshot))
  }
  const notifyRemoteStream = () => {
    remoteStreamListeners.forEach((listener) => listener(remoteStream))
  }
  const snapshotRemoteParticipants = (): RemoteMediaParticipant[] => Array.from(remoteParticipants.values()).map((entry) => {
    const audioTracks = entry.audioStream.getAudioTracks().filter((track) => track.readyState === "live")
    const videoTracks = entry.videoStream.getVideoTracks().filter((track) => track.readyState === "live")
    return {
      id: entry.id,
      name: entry.name,
      audioStream: audioTracks.length > 0 ? entry.audioStream : undefined,
      videoStream: videoTracks.length > 0 ? entry.videoStream : undefined,
      hasAudio: audioTracks.length > 0,
      hasVideo: videoTracks.length > 0,
    }
  }).filter((participant) => participant.hasAudio || participant.hasVideo)
  const notifyRemoteParticipants = () => {
    remoteParticipantListeners.forEach((listener) => listener(snapshotRemoteParticipants()))
  }
  const notifyRemoteMedia = () => {
    notifyRemoteStream()
    notifyRemoteParticipants()
  }
  const participantId = (participant: any): string => String(participant?.identity || participant?.sid || "remote")
  const ensureParticipant = (participant: any): RemoteParticipantEntry => {
    const id = participantId(participant)
    const current = remoteParticipants.get(id)
    if (current) {
      current.name = participant?.name || current.name
      return current
    }
    const entry = {
      id,
      name: participant?.name,
      audioStream: new MediaStream(),
      videoStream: new MediaStream(),
    }
    remoteParticipants.set(id, entry)
    return entry
  }
  const addRemoteTrack = (track: any, participant: any) => {
    const mediaTrack = track?.mediaStreamTrack
    if (!mediaTrack) return
    const entry = ensureParticipant(participant)
    const targetStream = mediaTrack.kind === "video" ? entry.videoStream : entry.audioStream
    if (!targetStream.getTracks().some((current) => current.id === mediaTrack.id)) {
      targetStream.addTrack(mediaTrack)
    }
    if (!remoteStream.getTracks().some((current) => current.id === mediaTrack.id)) {
      remoteStream.addTrack(mediaTrack)
    }
    logLiveKit("track subscribed", { participant: entry.id, kind: mediaTrack.kind, trackId: mediaTrack.id })
    notifyRemoteMedia()
  }
  const removeRemoteTrack = (track: any, participant: any) => {
    const mediaTrack = track?.mediaStreamTrack
    if (!mediaTrack) return
    const id = participantId(participant)
    const entry = remoteParticipants.get(id)
    const removeById = (stream: MediaStream) => {
      const existing = stream.getTracks().find((current) => current.id === mediaTrack.id)
      if (existing) stream.removeTrack(existing)
    }
    removeById(remoteStream)
    if (entry) {
      removeById(entry.audioStream)
      removeById(entry.videoStream)
      if (entry.audioStream.getTracks().length === 0 && entry.videoStream.getTracks().length === 0) {
        remoteParticipants.delete(id)
      }
    }
    logLiveKit("track unsubscribed", { participant: id, kind: mediaTrack.kind, trackId: mediaTrack.id })
    notifyRemoteMedia()
  }
  const removeParticipant = (participant: any) => {
    const id = participantId(participant)
    const entry = remoteParticipants.get(id)
    if (entry) {
      for (const track of [...entry.audioStream.getTracks(), ...entry.videoStream.getTracks()]) {
        remoteStream.removeTrack(track)
      }
      remoteParticipants.delete(id)
    }
    logLiveKit("participant disconnected", { participant: id })
    notifyRemoteMedia()
  }
  const room = new Room({ adaptiveStream: false, dynacast: true })
  let localStream: MediaStream | undefined
  const remoteStream = new MediaStream()
  try {
    if (!match.signaling.url || !match.signaling.ticket) throw new Error("LiveKit session is unavailable")
    const profile = await estimateCallMediaProfile(mode, apiBase)
    logLiveKit("media profile selected", profile)
    localStream = await requestCallMedia(mode)
    const localTracks = localStream.getTracks()
    room.on(RoomEvent.TrackSubscribed, (track: any, _publication: any, participant: any) => {
      addRemoteTrack(track, participant)
    })
    room.on(RoomEvent.TrackUnsubscribed, (track: any, _publication: any, participant: any) => {
      removeRemoteTrack(track, participant)
    })
    room.on(RoomEvent.TrackSubscriptionFailed, (_trackSid: any, participant: any, reason: any) => {
      logLiveKit("track subscription failed", { participant: participantId(participant), reason })
    })
    room.on(RoomEvent.ParticipantConnected, (participant: any) => {
      logLiveKit("participant connected", { participant: participantId(participant) })
      setPeerCount(room.remoteParticipants.size)
    })
    room.on(RoomEvent.ParticipantDisconnected, (participant: any) => {
      setPeerCount(room.remoteParticipants.size)
      removeParticipant(participant)
    })
    room.on(RoomEvent.Disconnected, () => {
      logLiveKit("room disconnected")
      remoteParticipants.clear()
      notifyRemoteMedia()
      setState("unavailable")
      setPeerCount(0)
    })
    room.on(RoomEvent.Reconnecting, () => {
      logLiveKit("room reconnecting")
      setState("reconnecting")
    })
    room.on(RoomEvent.Reconnected, () => {
      logLiveKit("room reconnected")
      setState("connected")
    })
    await room.connect(match.signaling.url, match.signaling.ticket)
    for (const track of localTracks) {
      const isVideoTrack = track.kind === "video"
      await room.localParticipant.publishTrack(track, isVideoTrack && profile.video
        ? {
            source: Track.Source.Camera,
            videoEncoding: {
              maxBitrate: profile.video.maxBitrate,
              maxFramerate: profile.video.frameRate,
              priority: profile.video.priority,
            },
            simulcast: profile.video.simulcast,
            degradationPreference: profile.video.degradationPreference,
            backupCodec: false,
          }
        : {
            source: Track.Source.Microphone,
            audioPreset: { maxBitrate: profile.audioMaxBitrate },
            dtx: true,
            red: profile.tier !== "constrained",
          })
    }
    for (const participant of room.remoteParticipants.values()) {
      for (const publication of participant.trackPublications.values()) {
        addRemoteTrack(publication.track, participant)
      }
    }
    setPeerCount(room.remoteParticipants.size)
    setState("connected")
    const syncLocalSource = async (kind: "audio" | "video", enabled: boolean) => {
      if (!localStream) localStream = new MediaStream()
      for (const track of localStream.getTracks().filter((track) => track.kind === kind && track.readyState !== "live")) {
        localStream.removeTrack(track)
      }
      const source = kind === "video" ? Track.Source.Camera : Track.Source.Microphone
      let publication = room.localParticipant.getTrackPublication(source)
      let mediaTrack = publication?.track?.mediaStreamTrack
      if (!enabled) {
        setMediaStreamEnabled(localStream, kind, false)
        if (kind === "video") await room.localParticipant.setCameraEnabled(false)
        else await room.localParticipant.setMicrophoneEnabled(false)
        notifyLocalStream()
        return
      }
      if (!mediaTrack || mediaTrack.readyState !== "live") {
        if (publication?.track) {
          await room.localParticipant.unpublishTrack(publication.track, true).catch(() => undefined)
        }
        publication = kind === "video"
          ? await room.localParticipant.setCameraEnabled(true)
          : await room.localParticipant.setMicrophoneEnabled(true)
        mediaTrack = publication?.track?.mediaStreamTrack
      }
      if (mediaTrack?.readyState === "live") {
        for (const track of localStream.getTracks().filter((track) => track.kind === kind && track.id !== mediaTrack?.id)) {
          localStream.removeTrack(track)
          if (track.readyState === "live") track.stop()
        }
        if (!localStream.getTracks().some((track) => track.id === mediaTrack?.id)) localStream.addTrack(mediaTrack)
        mediaTrack.enabled = true
      }
      notifyLocalStream()
    }
    return {
      get state() {
        return currentState
      },
      get peerCount() {
        return currentPeerCount
      },
      provider: "livekit",
      localStream,
      remoteStream,
      get remoteParticipants() {
        return snapshotRemoteParticipants()
      },
      onState(listener) {
        listener(currentState)
        stateListeners.add(listener)
        return () => stateListeners.delete(listener)
      },
      onLocalStreamChange(listener) {
        listener(localStream)
        localStreamListeners.add(listener)
        return () => localStreamListeners.delete(listener)
      },
      onRemoteStreamChange(listener) {
        listener(remoteStream)
        remoteStreamListeners.add(listener)
        return () => remoteStreamListeners.delete(listener)
      },
      onRemoteParticipantsChange(listener) {
        listener(snapshotRemoteParticipants())
        remoteParticipantListeners.add(listener)
        return () => remoteParticipantListeners.delete(listener)
      },
      onPeerCountChange(listener) {
        listener(currentPeerCount)
        peerCountListeners.add(listener)
        return () => peerCountListeners.delete(listener)
      },
      async setMicEnabled(enabled: boolean) {
        await syncLocalSource("audio", enabled)
      },
      async setCameraEnabled(enabled: boolean) {
        await syncLocalSource("video", enabled)
      },
      async resumeAfterInterruption(state) {
        await syncLocalSource("audio", state.mic)
        if (mode === "video") await syncLocalSource("video", state.camera)
      },
      async disconnect() {
        room.disconnect()
        stopMediaStream(localStream)
        stopMediaStream(remoteStream)
        setState("unavailable")
      },
    }
  } catch (error) {
    room.disconnect()
    stopMediaStream(localStream)
    stopMediaStream(remoteStream)
    return unavailableConnection(error instanceof Error ? error.message : "اتصال زنده برقرار نشد")
  }
}

async function shellConnection(sessionId: string, mode: Mode): Promise<RealtimeConnection> {
  try {
    const session = await joow.media.start({ sessionId, mode, requireCamera: mode === "video" })
    let currentState: ConnectionState = "connecting"
    const stateListeners = new Set<(state: ConnectionState) => void>()
    const setState = (state: ConnectionState) => {
      currentState = state
      stateListeners.forEach((listener) => listener(state))
    }
    const dispose = session.onState((state) => {
      setState(state === "live" ? "connected" : state === "ended" ? "unavailable" : state)
    })
    return {
      get state() {
        return currentState
      },
      provider: "host",
      localStream: session.localStream,
      remoteStream: session.remoteStream,
      onState(listener) {
        listener(currentState)
        stateListeners.add(listener)
        return () => stateListeners.delete(listener)
      },
      onRemoteStreamChange(listener) {
        listener(session.remoteStream)
        return () => {}
      },
      async setMicEnabled(enabled: boolean) {
        session.setMicEnabled(enabled)
      },
      async setCameraEnabled(enabled: boolean) {
        session.setCameraEnabled(enabled)
      },
      async disconnect() {
        dispose()
        session.stop()
      },
    }
  } catch (error) {
    return unavailableConnection(error instanceof Error ? error.message : "اتصال زنده برقرار نشد")
  }
}

async function joowcoreConnection(match: MatchFound, mode: Mode): Promise<RealtimeConnection> {
  let currentState: ConnectionState = "connecting"
  const stateListeners = new Set<(state: ConnectionState) => void>()
  const remoteStreamListeners = new Set<(stream?: MediaStream) => void>()
  const setState = (state: ConnectionState) => {
    currentState = state
    stateListeners.forEach((listener) => listener(state))
  }
  const notifyRemoteStream = () => {
    remoteStreamListeners.forEach((listener) => listener(remoteStream))
  }
  let socket: WebSocket | null = null
  let peer: RTCPeerConnection | null = null
  let localStream: MediaStream | undefined
  const remoteStream = new MediaStream()
  try {
    if (!match.signaling.ticket) throw new Error("شناسه تماس زنده در دسترس نیست")
    const delegated = isBridgeEmbeddedCandidate()
      ? await getRealtimeToken({ resourceType: "joowfak_session", resourceId: match.sessionId, ttlSeconds: 600 })
      : await getStandaloneRealtimeToken(match.sessionId)
    localStream = await requestCallMedia(mode) || undefined
    peer = new RTCPeerConnection({ iceServers: normalizeIceServers(match.media.iceServers) })
    localStream?.getTracks().forEach((track) => peer?.addTrack(track, localStream as MediaStream))
    peer.ontrack = (event) => {
      for (const track of event.streams[0]?.getTracks() || [event.track]) {
        if (!remoteStream.getTracks().some((current) => current.id === track.id)) {
          remoteStream.addTrack(track)
          notifyRemoteStream()
        }
      }
    }
    const wsUrl = signalingWebSocketURL(match.signaling.url, delegated.realtimeApiBaseUrl)
    wsUrl.searchParams.set("appId", "joowfak")
    wsUrl.searchParams.set("callId", match.signaling.ticket)
    wsUrl.searchParams.set("access_token", delegated.accessToken)
    socket = await openSocket(wsUrl.toString())
    const activePeer = peer
    activePeer.onicecandidate = (event) => {
      if (event.candidate && socket?.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ type: "media.ice", callId: match.signaling.ticket, payload: event.candidate.toJSON() }))
      }
    }
    socket.addEventListener("message", (event) => {
      void handleRealtimeEvent(event, activePeer, socket as WebSocket, match.signaling.ticket, (state) => {
        setState(state)
      })
    })
    socket.addEventListener("close", () => {
      if (currentState === "connected") setState("reconnecting")
    })
    socket.addEventListener("error", () => {
      setState("failed")
    })
    const offer = await activePeer.createOffer()
    await activePeer.setLocalDescription(offer)
    socket.send(JSON.stringify({
      type: "call.join",
      callId: match.signaling.ticket,
      payload: {
        offerSdp: offer.sdp || "",
        isMuted: !localStream?.getAudioTracks().some((track) => track.enabled),
        isVideoOn: mode === "video" && Boolean(localStream?.getVideoTracks().some((track) => track.enabled)),
      },
    }))
    return {
      get state() {
        return currentState
      },
      provider: "joowcore",
      localStream,
      remoteStream,
      onState(listener) {
        listener(currentState)
        stateListeners.add(listener)
        return () => stateListeners.delete(listener)
      },
      onRemoteStreamChange(listener) {
        listener(remoteStream)
        remoteStreamListeners.add(listener)
        return () => remoteStreamListeners.delete(listener)
      },
      async setMicEnabled(enabled: boolean) {
        setMediaStreamEnabled(localStream, "audio", enabled)
        sendTrackState(socket, match.signaling.ticket, "audio", enabled)
      },
      async setCameraEnabled(enabled: boolean) {
        setMediaStreamEnabled(localStream, "video", enabled)
        sendTrackState(socket, match.signaling.ticket, "video", enabled)
      },
      async disconnect() {
        if (socket?.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({ type: "call.leave", callId: match.signaling.ticket }))
        }
        socket?.close()
        peer?.close()
        stopMediaStream(localStream)
        stopMediaStream(remoteStream)
        setState("unavailable")
      },
    }
  } catch (error) {
    socket?.close()
    peer?.close()
    stopMediaStream(localStream)
    stopMediaStream(remoteStream)
    return unavailableConnection(error instanceof Error ? error.message : "اتصال زنده برقرار نشد")
  }
}

async function getStandaloneRealtimeToken(sessionId: string): Promise<{ accessToken: string; realtimeApiBaseUrl?: string }> {
  const authorization = authAuthorizationHeader()
  const response = await fetch(`${apiBase}/realtime/token`, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      ...(authorization ? { Authorization: authorization } : {}),
    },
    body: JSON.stringify({ resourceType: "joowfak_session", resourceId: sessionId }),
  })
  if (!response.ok) throw new Error(`Realtime token request failed: ${response.status}`)
  return response.json()
}

async function mockConnection(mode: Mode): Promise<RealtimeConnection> {
  const localStream = await requestCallMedia(mode)
  const remoteParticipantCount = Math.max(1, Math.min(5, Number(sessionStorage.getItem("joowfak.mockRemoteParticipants") || "1") || 1))
  const remoteParticipants = Array.from({ length: remoteParticipantCount }, (_value, index) => {
    const stream = localStream?.clone() || new MediaStream()
    const audioTracks = stream.getAudioTracks()
    const videoTracks = stream.getVideoTracks()
    return {
      id: index === 0 ? "part_mock_peer" : `part_mock_extra_${index}`,
      audioStream: audioTracks.length > 0 ? new MediaStream(audioTracks) : undefined,
      videoStream: videoTracks.length > 0 ? new MediaStream(videoTracks) : undefined,
      hasAudio: audioTracks.length > 0,
      hasVideo: videoTracks.length > 0,
    } satisfies RemoteMediaParticipant
  })
  const remoteStream = new MediaStream(remoteParticipants.flatMap((participant) => [
    ...(participant.audioStream?.getTracks() || []),
    ...(participant.videoStream?.getTracks() || []),
  ]))
  const delayRemoteStream = sessionStorage.getItem("joowfak.mockDelayedRemoteStream") === "true"
  const remoteStreamListeners = new Set<(stream?: MediaStream) => void>()
  let visibleRemoteStream: MediaStream | undefined = delayRemoteStream ? undefined : remoteStream
  const remoteStreamTimer = delayRemoteStream ? window.setTimeout(() => {
    visibleRemoteStream = remoteStream
    remoteStreamListeners.forEach((listener) => listener(visibleRemoteStream))
  }, 2600) : undefined
  return {
    state: "connected",
    provider: "mock",
    localStream,
    get remoteStream() {
      return visibleRemoteStream
    },
    remoteParticipants,
    peerCount: remoteParticipantCount,
    onState(listener) {
      listener("connected")
      return () => {}
    },
    onRemoteStreamChange(listener) {
      remoteStreamListeners.add(listener)
      listener(visibleRemoteStream)
      return () => remoteStreamListeners.delete(listener)
    },
    onRemoteParticipantsChange(listener) {
      listener(remoteParticipants)
      return () => {}
    },
    onPeerCountChange(listener) {
      listener(remoteParticipantCount)
      return () => {}
    },
    async setMicEnabled(enabled: boolean) {
      setMediaStreamEnabled(localStream, "audio", enabled)
    },
    async setCameraEnabled(enabled: boolean) {
      setMediaStreamEnabled(localStream, "video", enabled)
    },
    async disconnect() {
      if (remoteStreamTimer !== undefined) window.clearTimeout(remoteStreamTimer)
      remoteStreamListeners.clear()
      stopMediaStream(localStream)
      stopMediaStream(remoteStream)
    },
  }
}

function unavailableConnection(error: string): RealtimeConnection {
  return {
    state: "unavailable",
    provider: "unavailable",
    peerCount: 0,
    error,
    onState(listener) {
      listener("unavailable")
      return () => {}
    },
    onRemoteStreamChange(listener) {
      listener(undefined)
      return () => {}
    },
    onPeerCountChange(listener) {
      listener(0)
      return () => {}
    },
    async setMicEnabled() {},
    async setCameraEnabled() {},
    async disconnect() {},
  }
}

function normalizeIceServers(values: MatchFound["media"]["iceServers"]): RTCIceServer[] {
  return values.map((value) => ({
    urls: value.urls,
    username: value.username,
    credential: value.credential,
  }))
}

function signalingWebSocketURL(signalingURL: string, realtimeApiBaseUrl?: string): URL {
  const source = signalingURL || "/api/v1/ws"
  const base = realtimeApiBaseUrl || window.location.origin
  const url = new URL(source, base)
  url.protocol = url.protocol === "https:" ? "wss:" : url.protocol === "http:" ? "ws:" : url.protocol
  return url
}

function openSocket(url: string): Promise<WebSocket> {
  return new Promise((resolve, reject) => {
    const socket = new WebSocket(url)
    const cleanup = () => {
      socket.removeEventListener("open", opened)
      socket.removeEventListener("error", failed)
    }
    const opened = () => {
      cleanup()
      resolve(socket)
    }
    const failed = () => {
      cleanup()
      socket.close()
      reject(new Error("اتصال زنده باز نشد"))
    }
    socket.addEventListener("open", opened)
    socket.addEventListener("error", failed)
  })
}

async function handleRealtimeEvent(event: MessageEvent, peer: RTCPeerConnection, socket: WebSocket, callId: string, setState: (state: ConnectionState) => void): Promise<void> {
  const payload = JSON.parse(String(event.data || "{}")) as Record<string, unknown>
  if (payload.type === "media.answer" && typeof payload.answerSdp === "string") {
    await peer.setRemoteDescription({ type: "answer", sdp: payload.answerSdp })
    setState("connected")
    return
  }
  if (payload.type === "media.ice") {
    const candidate = (payload.candidate || payload.payload) as RTCIceCandidateInit | undefined
    if (candidate) await peer.addIceCandidate(candidate)
    return
  }
  if (payload.type === "media.renegotiation.offer" && typeof payload.offerSdp === "string") {
    await peer.setRemoteDescription({ type: "offer", sdp: payload.offerSdp })
    const answer = await peer.createAnswer()
    await peer.setLocalDescription(answer)
    sendRealtimeCommand(socket, callId, "media.renegotiation.answer", { answerSdp: answer.sdp || "" })
    return
  }
  if (payload.type === "session.revoked" || payload.type === "call.error") {
    setState("failed")
  }
}

function sendTrackState(socket: WebSocket | null, callId: string, track: "audio" | "video", enabled: boolean): void {
  if (socket?.readyState === WebSocket.OPEN) {
    sendRealtimeCommand(socket, callId, "media.track_state", { track, enabled })
  }
}

function sendRealtimeCommand(socket: WebSocket, callId: string, type: string, payload?: unknown): void {
  if (socket.readyState === WebSocket.OPEN) {
    socket.send(JSON.stringify({ type, callId, payload }))
  }
}
